import React from "react";
import { Link } from "react-router-dom";
import { Box, Chip, Button, Grid } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import currentOpenings from "../../../api/currentOpeningsData.json";

const CurrentOpeningSection = () => {
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const themeColors = {
    primary: "var(--primary-color)",
    secondary: "var(--secondary-color)",
    midBlue: "#687693",
    lightGray: "#f1f4f9",
    white: "#fff",
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
        {currentOpenings.map((cop) => (
          <Accordion
            key={cop.id}
            expanded={expanded === cop.id}
            onChange={handleChange(cop.id)}
            elevation={0}
            sx={{
              marginBottom: "20px",
              borderRadius: "20px !important",
              border: `1px solid #e0e6ed`,
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:before": { display: "none" },
              "&:hover": {
                borderColor: themeColors.primary,
                boxShadow: `0 10px 30px rgba(26, 66, 161, 0.08)`,
              },
              "&.Mui-expanded": {
                margin: "0 0 24px 0",
                borderColor: themeColors.primary,
                boxShadow: `0 20px 40px rgba(26, 66, 161, 0.12)`,
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ color: themeColors.primary, fontSize: "28px" }} />
              }
              sx={{
                px: { xs: 2, md: 4 },
                py: 2,
                minHeight: "80px !important",
                bgcolor: '#fff',
                borderLeft: `6px solid transparent`,
                transition: "all 0.3s ease",
                "&.Mui-expanded": {
                  borderLeft: `6px solid ${themeColors.primary}`,
                  bgcolor: 'rgba(26, 66, 161, 0.02)',
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, width: "100%" }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: '12px', 
                  bgcolor: 'rgba(26, 66, 161, 0.05)', 
                  color: themeColors.primary,
                  display: { xs: 'none', sm: 'flex' }
                }}>
                  <WorkIcon sx={{ fontSize: "24px" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: themeColors.primary,
                    fontFamily: "'Roboto Slab', serif",
                  }}
                >
                  {cop.name}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 2, md: 5 }, pb: 5, pt: 2 }}>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                  { icon: <LocationOnIcon />, label: "Location", value: cop.location },
                  { icon: <SchoolIcon />, label: "Qualification", value: cop.qualification },
                  { icon: <WorkIcon />, label: "Experience", value: cop.experience }
                ].map((item, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Box sx={{ 
                      p: 2.5, 
                      bgcolor: '#f8fbff', 
                      borderRadius: '16px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      border: '1px solid rgba(26, 66, 161, 0.05)'
                    }}>
                      <Box sx={{ color: themeColors.primary }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontSize: 16 ,color: themeColors.primary, fontWeight: 700 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{fontSize: 14, color: themeColors.tertiary , fontWeight: 500 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: themeColors.primary , mb: 2, fontFamily: "'Roboto Slab', serif" }}>
                  Key Responsibilities & Skills
                </Typography>
                <Grid container spacing={2}>
                  {cop.RequirementsAndSkills.map((skill, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <CheckCircleIcon sx={{ color: themeColors.primary, fontSize: 20, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ color: themeColors.midBlue, lineHeight: 1.6 }}>
                          {skill}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ 
                p: 3, 
                borderRadius: '16px', 
                bgcolor: themeColors.primary, 
                color: '#fff', 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 3,
                mb: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmailIcon sx={{ fontSize: 30 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, color:'#fff' }}>
                    Email us your resume at 
                    <a href="mailto:admin@vdoogar.com" style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600, marginLeft: '8px' }}>
                      admin@vdoogar.com
                    </a>
                  </Typography>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', height: '30px', bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Typography variant="body2" sx={{ opacity: 0.8, color:'#fff' }}>
                  Mention "{cop.name}" in the subject line.
                </Typography>
              </Box>

              <Button
                component={Link}
                to="/applyNow"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: themeColors.primary,
                  color: '#fff',
                  px: 5,
                  py: 1.8,
                  borderRadius: '12px',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 10px 20px rgba(26, 66, 161, 0.2)',
                  '&:hover': {
                    bgcolor: themeColors.primary,
                    filter: 'brightness(1.1)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 30px rgba(26, 66, 161, 0.3)',
                  }
                }}
              >
                Apply Online Now
              </Button>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default CurrentOpeningSection;
